import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { EmailSchema } from '@vestigas/utils';
import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

const zodAddInvoiceAllowedSenderAddressPayloadSchema = z.object({
  email: EmailSchema,
});

type AddInvoiceAllowedSenderAddressVariables = {
  companyId: UUID;
  email: string;
};

/**
 * Creates a new invoice allowed sender address for a company.
 *
 * @param variables - The variables needed for the mutation.
 * @param variables.companyId - The ID of the company.
 * @param variables.email - The email address to add.
 * @returns A promise that resolves when the invoice allowed sender address is created.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/add_invoice_allowed_sender_address_vadmin_company__company_id__invoice_allowed_sender_address_post
 */
// fallow-ignore-next-line unused-export
export const addCompanyInvoiceAllowedSenderAddress = async ({
  companyId,
  email,
}: AddInvoiceAllowedSenderAddressVariables) => {
  try {
    return await vestigasApi
      .post(ENDPOINT.COMPANY.INVOICE_ALLOWED_SENDER_ADDRESS.CREATE(companyId), {
        json: { email },
        validate: {
          body: zodAddInvoiceAllowedSenderAddressPayloadSchema,
          response: zodSuccessResponseSchema,
        },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error adding invoice allowed sender address',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query mutation hook for adding an invoice allowed sender address to a company.
 *
 * @param options - Additional options for the useMutation hook.
 * @returns The result of the useMutation hook.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/add_invoice_allowed_sender_address_vadmin_company__company_id__invoice_allowed_sender_address_post
 */
export const useMutationCompanyAddInvoiceAllowedSenderAddress = (
  options?: Omit<
    UseMutationOptions<
      SuccessResponse,
      Error,
      AddInvoiceAllowedSenderAddressVariables
    >,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    SuccessResponse,
    Error,
    AddInvoiceAllowedSenderAddressVariables
  >({
    mutationFn: addCompanyInvoiceAllowedSenderAddress,
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getInvoiceAllowedSenderAddresses(
          variables.companyId,
        ),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
};
